import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Radar, Brain, Bell, Flag, ScanLine } from "lucide-react";
import { Seo } from "@/components/web/Seo";
import { MaskedLines, FadeUp } from "@/components/web/Reveal";
import { EditorialMarquee } from "@/components/web/EditorialMarquee";
import { HomeFaq } from "@/components/web/HomeFaq";
import { MobileAppCta } from "@/components/web/MobileAppCta";

const HERO_IMG =
  "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRpZ2l0YWwlMjBuZXR3b3JrJTIwZGFya3xlbnwwfHx8fDE3ODY2NDk2MjV8MA&ixlib=rb-4.1.0&q=75&w=1920";
const BENTO_IMG =
  "https://images.unsplash.com/photo-1510915228340-29c85a43dcfe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwyfHxjeWJlciUyMHNlY3VyaXR5JTIwZGFya3xlbnwwfHx8fDE3ODY2NDk2MjV8MA&ixlib=rb-4.1.0&q=85";

const MANIFESTO = [
  {
    n: "01",
    title: "Every link is a door.",
    body: "Phishing kits, scam storefronts and malware droppers hide behind ordinary-looking URLs. One tap is all it takes. We believe no one should have to guess whether a link is safe.",
  },
  {
    n: "02",
    title: "AI reads what you can't.",
    body: "LinkExpln inspects the anatomy of a URL — domain reputation, redirect chains, lookalike characters, threat-intelligence signals — in the time it takes you to blink.",
  },
  {
    n: "03",
    title: "Protection should be effortless.",
    body: "No manuals. No security degree required. Paste a link, get a verdict. Clear, human answers: safe, suspicious, or dangerous — and why.",
  },
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const heroFade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <>
      <Seo
        title="LinkExpln — AI-Powered Link Security"
        description="LinkExpln protects you from scams, phishing and malicious links. AI-powered URL analysis that tells you if a link is safe — before you click."
      />

      <section ref={heroRef} data-testid="hero-section" className="relative min-h-[92vh] flex items-end overflow-hidden ls-noise">
        <motion.div className="absolute inset-0" style={{ y: bgY }} aria-hidden="true">
          <img src={HERO_IMG} alt="" className="w-full h-[120%] object-cover" />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030711] via-transparent to-black/40" />
        </motion.div>

        <motion.div style={{ opacity: heroFade }} className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pb-20 pt-36">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="ls-overline mb-6"
            data-testid="hero-overline"
          >
            AI-Powered Link Security
          </motion.p>

          <h1
            data-testid="hero-heading"
            className="font-heading font-black tracking-tighter text-white text-5xl sm:text-7xl lg:text-8xl leading-[0.95] text-balance"
          >
            <MaskedLines lines={["EVERY LINK", "IS A DOOR."]} delay={0.15} />
            <MaskedLines lines={["WE GUARD IT."]} delay={0.45} lineClassName="text-[#00C5FE]" />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-8 max-w-xl text-zinc-300 text-base sm:text-lg leading-relaxed"
          >
            LinkExpln analyzes URLs for phishing, scams and malware —
            so you know a link is safe before you ever click it.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/scanner"
              data-testid="hero-cta-scan"
              className="inline-flex items-center justify-center gap-2 btn-brand text-black font-heading font-bold px-8 py-4 text-base transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#00C5FE] focus:ring-offset-2 focus:ring-offset-black"
            >
              Scan a Link <ArrowRight size={18} strokeWidth={2.5} />
            </Link>
            <Link
              to="/how-it-works"
              data-testid="hero-cta-how"
              className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-[#00C5FE] hover:text-[#00C5FE] text-white font-heading font-semibold px-8 py-4 text-base transition-colors duration-200"
            >
              How It Works <ArrowUpRight size={18} />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      <EditorialMarquee />

      <section data-testid="manifesto-section" className="py-24 sm:py-32 bg-[#030711]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="ls-overline mb-16">The Manifesto</p>
          </FadeUp>
          <div className="space-y-24">
            {MANIFESTO.map((c, i) => (
              <FadeUp key={c.n} delay={0.05 * i}>
                <div className={`grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start ${i % 2 === 1 ? "md:text-right" : ""}`}>
                  <div className={`md:col-span-3 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                    <span className="font-mono-ls font-light text-7xl sm:text-8xl text-white/10 leading-none">{c.n}</span>
                  </div>
                  <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-1 md:col-start-2 md:flex md:flex-col md:items-end" : ""}`}>
                    <h2 className="font-heading font-light text-3xl sm:text-4xl text-white tracking-tight text-balance">
                      {c.title}
                    </h2>
                    <p className={`mt-5 text-zinc-400 leading-relaxed max-w-lg ${i % 2 === 1 ? "md:text-right" : ""}`}>{c.body}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="bento-section" className="py-24 sm:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <FadeUp>
            <p className="ls-overline mb-4">Capabilities</p>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter text-white mb-16 text-balance">
              A shield built for the modern web.
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-px bg-white/10 border border-white/10">
            <FadeUp className="md:col-span-7 bg-[#0A121D] p-10 group relative overflow-hidden min-h-[320px]">
              <img src={BENTO_IMG} alt="Security analyst at work" className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-40 group-hover:scale-105 transition-[opacity,transform] duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="relative z-10 h-full flex flex-col justify-end pt-32">
                <Radar className="text-[#00C5FE] mb-4" size={28} />
                <h3 className="font-heading font-bold text-2xl text-white">Real-Time URL Analysis</h3>
                <p className="mt-3 text-zinc-400 leading-relaxed max-w-md">
                  Every scan dissects the link — domain age, redirect chains, SSL posture and known threat signatures.
                </p>
              </div>
            </FadeUp>

            <FadeUp delay={0.1} className="md:col-span-5 bg-[#0A121D] p-10 hover:bg-[#101A28] transition-colors duration-300">
              <Brain className="text-[#00C5FE] mb-4" size={28} />
              <h3 className="font-heading font-bold text-2xl text-white">AI Threat Intelligence</h3>
              <p className="mt-3 text-zinc-400 leading-relaxed">
                Machine-learning models trained on phishing and scam patterns catch lookalike domains and zero-day campaigns that static blocklists miss.
              </p>
            </FadeUp>

            <FadeUp delay={0.15} className="md:col-span-4 bg-[#0A121D] p-10 hover:bg-[#101A28] transition-colors duration-300">
              <Bell className="text-[#00C5FE] mb-4" size={28} />
              <h3 className="font-heading font-bold text-xl text-white">Instant Verdicts</h3>
              <p className="mt-3 text-zinc-400 leading-relaxed text-sm">
                Safe, suspicious, or dangerous — with a plain-language explanation of why.
              </p>
            </FadeUp>

            <FadeUp delay={0.2} className="md:col-span-4 bg-[#0A121D] p-10 hover:bg-[#101A28] transition-colors duration-300">
              <Flag className="text-[#00C5FE] mb-4" size={28} />
              <h3 className="font-heading font-bold text-xl text-white">Community Reporting</h3>
              <p className="mt-3 text-zinc-400 leading-relaxed text-sm">
                Spotted something suspicious? Report a link and help protect everyone else.
              </p>
            </FadeUp>

            <FadeUp delay={0.25} className="md:col-span-4 bg-[#0A121D] p-10 hover:bg-[#101A28] transition-colors duration-300">
              <ScanLine className="text-[#00C5FE] mb-4" size={28} />
              <h3 className="font-heading font-bold text-xl text-white">Built for Everyone</h3>
              <p className="mt-3 text-zinc-400 leading-relaxed text-sm">
                No security expertise needed. Paste, scan, decide — in seconds.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      <MobileAppCta />

      <HomeFaq />

      <section data-testid="final-cta-section" className="py-24 sm:py-32 border-t border-white/10 relative overflow-hidden ls-noise">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 text-left">
          <FadeUp>
            <p className="ls-overline mb-6">Get Protected</p>
            <h2 className="font-heading font-black text-4xl sm:text-6xl tracking-tighter text-white text-balance max-w-3xl">
              Think before you click.
              <span className="text-[#00C5FE]"> Better yet — scan.</span>
            </h2>
            <div className="mt-10">
              <Link
                to="/scanner"
                data-testid="final-cta-scan"
                className="inline-flex items-center gap-2 btn-brand text-black font-heading font-bold px-8 py-4 text-base transition-colors duration-200"
              >
                Open the Scanner <ArrowRight size={18} strokeWidth={2.5} />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
};

export default Home;
